// Decision 103 a (owner, 30 Oct 2026): "Make sure a new deploy reaches an open phone without a manual cache clear."
// Decision 129 #2 (owner, 31 Oct 2026): "when a new release is live, the app shows 'New version ready, tap to update'
// within a minute of being opened or brought to the front, and the release number in More always matches what is
// running." On 31 Oct the installed app stayed on Release 367 after 379 was live, with no bar: it learnt of a release
// only through the service worker, checked on coming to the front and hourly, and only once the new worker had finished
// downloading; with no service worker nothing was ever checked.
//
// Two signals, either of which shows the bar (offline/UpdateBar.tsx, rendered by AppShell on every screen):
// 1. The service worker (Decision 103). vite-plugin-pwa, registerType "prompt": a new worker installs and WAITS; it never
//    swaps itself in under a running page. A worker waiting while another is active is a new version.
// 2. The version file (Decision 129). Every release's build carries /version.json, {"release", "commit"}, written by
//    tools/write-version.mjs in the deploy, served no-store and never precached. A release number above this build's
//    VITE_RELEASE_RUN is a newer release, whether or not its worker has finished downloading, and whether or not this
//    page has a service worker at all (an iPhone with its storage cleared, WebKit's limits). A dev build (no release
//    number), the same or an older number, or a file that is missing, failing or not the file: nothing.
// When it checks: the version file at open; both signals each time the app comes to the front and every minute while in
// view; never while hidden or while the browser says there is no connection; and within seconds of the signal coming
// back ("online"). More keeps naming the build's own release (screens/More.tsx), never the file's.
//
// The tap, once per page, never a reload the person did not ask for (what is not yet in the outbox, a half-filled form,
// is never lost; the outbox, the waiting files and the cache are in IndexedDB, offline/db.ts, which a reload keeps):
// a. a new worker waiting: {type: "SKIP_WAITING"} to it (the plugin's updateSW(true)); this page reloads once when it
//    takes control ("controllerchange"), or after 10 s anyway (a worker that never takes control);
// b. a worker WAS waiting and another tab already activated it: reload at once, it controls the page now (Decision 103);
// c. only the version file knows (no worker waiting yet, none has been): never a reload under the old worker, which
//    would bring the old build back from its precache. The registration is asked for the new worker, which is told to
//    take over as soon as it is installed, and the page reloads once it controls it. A worker already downloading when
//    the page registers is watched too, not only one announced later. If none arrives, the page reloads anyway after
//    20 s, or after 30 s at the latest when one is still downloading or only just waiting (contract 8b: within 30 s of
//    the tap); an install that fails reloads at once. While it downloads the bar says so ("Getting the new version…"),
//    and the page a 30 s reload brings back says why the bar is back (UpdateBar.tsx). A worker told to take over that
//    never does (a held activation): the page reloads anyway at 35 s, under the old worker, as a last resort: never at
//    30 s, where the reload must not run under a worker that has the message;
// d. no service worker controls the page: reload at once, the network serves the new build.
//
// Kept from the Decision 103 reviews (offline/decision-103-app-update.test.tsx pins each):
// - The plugin reloads only when the page HAD a controlling worker when it registered (workbox-window's isUpdate), so the
//   tap also reloads on navigator.serviceWorker's own "controllerchange"; onNeedReload is given, so only the tab whose
//   bar was tapped reloads, never one in the background.
// - workbox-window stops listening for new versions after the first one it judges external, so the bar does not rely on
//   onNeedRefresh: a worker waiting on the registration shows it, at registration, after each check, and when an
//   install the registration reports finishes. A worker waiting with NOTHING active is a first install, not a new version.
// - A quick double tap arms one listener and one fallback timer, not two. No update() at registration: the minute's
//   check follows soon enough, and the checks' counts stay exact.
import { registerSW } from "virtual:pwa-register";

type Listener = () => void;

/** sessionStorage key: the last reload left the new version still downloading (read once by the next page, UpdateBar.tsx). */
const STILL_DOWNLOADING_KEY = "rk-update-still-downloading";
/** ... and how long that note stays true: a minute or two is the point; an app left in the background is not told it later. */
const STILL_DOWNLOADING_FOR = 5 * 60 * 1000;

/** Both signals are checked this often while the app is in view (Decision 129 #2: the bar within a minute). */
const EVERY = 60 * 1000;
/** A version file request that has not answered by then is given up (a farm signal): the next check asks again. */
const FILE_TIMEOUT = 15 * 1000;
/** After a tap with a worker waiting: how long to wait for it to take control before reloading anyway. */
const FALLBACK_WAITING = 10 * 1000;
/** After a tap when only the version file knows: how long to wait for the new worker before reloading anyway, ... */
const FALLBACK_FETCHING = 20 * 1000;
/** ... and how much longer when the new worker is still downloading at that point (20 s + 10 s = 30 s in all). */
const FALLBACK_INSTALLING = 10 * 1000;
/** ... and the last resort for a new worker that was told to take over and never did (20 s + 15 s: the tap's reload comes at 35 s). */
const FALLBACK_TOLD = 15 * 1000;

let started = false;
/** A new worker is, or was, installed and waiting on this page (Decision 103): the bar shows. */
let workerWaiting = false;
/** The version file named a release newer than this build (Decision 129): the bar shows until the page reloads. */
let newerRelease = false;
let tapped = false;
let reloaded = false;
/** The tap found no worker waiting: the next worker that is installed and waiting is told to take over at once. */
let awaitingWorker = false;
/** The tap is waiting for the new version to download (path c): the bar says so instead of "Reloading…". */
let gettingNewVersion = false;
/** The page before this one reloaded at the 30 s mark with the new version still downloading: the bar says why it is back. */
let stillDownloading = false;
/** Workers whose install is already watched (a worker seen at registration and announced again by "updatefound"). */
const watched = new WeakSet<object>();
let registration: ServiceWorkerRegistration | undefined;
/**
 * window.location as it was when the bar was tapped. In a browser it is the same object as window.location (a reload
 * ends the page, timers and all); it is kept so the fallback timer reloads the location it was armed for even where
 * window.location is replaced later (the tests give each page its own).
 */
let tapLocation: Location | undefined;
let updateSW: ((reload?: boolean) => Promise<void>) | null = null;
const listeners = new Set<Listener>();

function notify() {
  for (const l of listeners) l();
}

function setWorkerWaiting() {
  if (workerWaiting) return;
  workerWaiting = true;
  notify();
}

function setNewerRelease() {
  if (newerRelease) return;
  newerRelease = true;
  notify();
}

/** This build's release number (tools/deploy.sh bakes VITE_RELEASE_RUN in), or null on a dev build. */
function runningRelease(): number | null {
  const run = (import.meta.env.VITE_RELEASE_RUN ?? "").trim();
  if (!run) return null;
  const n = Number(run);
  return Number.isFinite(n) ? n : null;
}

/** Reloads this page once, and only after its own bar was tapped. `leftDownloading`: the new version is not in yet. */
function reloadOnce(leftDownloading = false) {
  if (!tapped || reloaded) return;
  reloaded = true;
  if (gettingNewVersion) {
    gettingNewVersion = false;
    notify();
  }
  if (leftDownloading) rememberStillDownloading();
  (tapLocation ?? window.location).reload();
}

/** Tells the page that opens after this reload that the new version was still downloading (the bar then says why it is back). */
function rememberStillDownloading() {
  try {
    sessionStorage.setItem(STILL_DOWNLOADING_KEY, String(Date.now()));
  } catch {
    // No storage here: the bar simply stays silent about it.
  }
}

/** Reads, once, whether the last reload left the new version still downloading, and forgets it. */
function recallStillDownloading() {
  try {
    const at = Number(sessionStorage.getItem(STILL_DOWNLOADING_KEY));
    sessionStorage.removeItem(STILL_DOWNLOADING_KEY);
    if (Number.isFinite(at) && at > 0 && Date.now() - at < STILL_DOWNLOADING_FOR) stillDownloading = true;
  } catch {
    // No storage here: nothing to recall.
  }
}

/**
 * Asks the waiting worker to take over: {type: "SKIP_WAITING"}, posted to it here. The plugin's updateSW(true) does the
 * same but through workbox-window, which only knows a registration once register() has answered (a page that opened
 * mid-install gets that answer when the install is over) and a worker it saw install; posted from here it works for
 * both. When there is no worker to post to, the plugin is asked; when that fails, reload anyway.
 */
function skipWaiting() {
  const w = registration?.waiting;
  if (w && typeof w.postMessage === "function") {
    w.postMessage({ type: "SKIP_WAITING" });
    return;
  }
  updateSW?.(true).catch(() => reloadOnce());
}

/**
 * A worker waiting on the registration shows the bar, whether or not the plugin's onNeedRefresh fired, but only while
 * another worker is already active: a worker waiting with nothing active is the page's first install, not a new version.
 * After a tap that found nothing waiting, the worker that arrives is asked to take over at once.
 */
function showIfWaiting() {
  const r = registration;
  if (!r?.waiting || !r.active) return;
  setWorkerWaiting();
  if (awaitingWorker) {
    awaitingWorker = false;
    gettingNewVersion = false; // it is downloaded: from here the bar says "Reloading…"
    notify();
    skipWaiting();
  }
}

/** Watches one installing worker: installed and waiting shows the bar (and, after a tap, takes over); failing ends the wait. */
function watchInstall(r: ServiceWorkerRegistration, w: ServiceWorker | null) {
  if (!w || typeof w.addEventListener !== "function" || watched.has(w)) return;
  watched.add(w);
  w.addEventListener("statechange", () => {
    if (w.state === "installed") showIfWaiting();
    // The worker the tap was waiting for failed to install (the signal went mid-precache): nothing more is coming.
    else if (w.state === "redundant" && awaitingWorker && !r.waiting && !r.installing) reloadOnce();
  });
}

/**
 * A new version the registration reports installing shows the bar as soon as it is installed and waiting. A worker that
 * is already installing when the page registers (the page after a 30 s reload; an open whose own check started the
 * install) is watched too: "updatefound" was fired before this page listened.
 */
function watchInstalls(r: ServiceWorkerRegistration) {
  if (typeof r.addEventListener !== "function") return;
  r.addEventListener("updatefound", () => watchInstall(r, r.installing));
  watchInstall(r, r.installing);
}

/** The version file: a release number above this build's shows the bar. Never from a cache; a failure is silent. */
async function checkVersionFile() {
  const running = runningRelease();
  if (running === null || typeof fetch !== "function") return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const ctrl = new AbortController();
    timer = setTimeout(() => ctrl.abort(), FILE_TIMEOUT);
    const res = await fetch("/version.json", { cache: "no-store", signal: ctrl.signal });
    if (!res.ok) return;
    const body = (await res.json()) as { release?: unknown } | null;
    const release = Number(String(body?.release ?? "").trim());
    if (Number.isFinite(release) && release > running) setNewerRelease();
  } catch {
    // Offline, given up, or not the version file (a dev server's index.html): the next check asks again.
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** The service worker: asks the browser for a new worker; one that is installed and waiting shows the bar. */
function checkServiceWorker() {
  const r = registration;
  if (!r) return;
  try {
    void Promise.resolve(r.update()).then(showIfWaiting, () => undefined); // no signal: the next check asks again
  } catch {
    // The same.
  }
}

/** One check of both signals, only while the app is in view and the browser says it has a connection. */
function check() {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
  // No connection (navigator.onLine false is reliable; true is not): a check could only fail, and the "online" event
  // brings the next one within seconds of the signal coming back.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  void checkVersionFile();
  checkServiceWorker();
}

/**
 * Only the version file knows (no worker waiting on this page, none has been): asks the registration for the new worker,
 * which showIfWaiting() tells to take over as soon as it is installed and waiting. Nothing happens here when the
 * registration is not known yet; onRegisteredSW comes back to it.
 */
function pursueNewWorker() {
  const r = registration;
  if (!r || reloaded) return;
  awaitingWorker = true;
  if (r.waiting) {
    showIfWaiting();
    return;
  }
  // A worker already downloading (the check that showed the bar started it) is waited for; otherwise ask for one.
  if (r.installing) return;
  try {
    void Promise.resolve(r.update()).then(showIfWaiting, () => reloadOnce()); // the browser could not even ask: nothing is coming
  } catch {
    reloadOnce();
  }
}

/** The registration this page works with: watched for new workers, and gone on with when the bar was already tapped. */
function takeRegistration(r: ServiceWorkerRegistration) {
  const first = registration !== r;
  registration = r;
  if (first) watchInstalls(r);
  showIfWaiting();
  // Tapped before the registration was known (the version file showed the bar): go on with the update now.
  if (first && tapped) pursueNewWorker();
}

/**
 * The browser hands a page its registration (register()) only when the install in progress has finished, so a page that
 * opens while the new worker is downloading would not know it for the whole download. getRegistration() answers at once.
 */
function adoptRegistration() {
  const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  if (registration || typeof sw?.getRegistration !== "function") return;
  void Promise.resolve(sw.getRegistration()).then((r) => { if (r) takeRegistration(r); }, () => undefined);
}

/** Registers the service worker once per page and looks for a new release whenever the app is in view. */
export function startAppUpdates(): void {
  if (started) return;
  started = true;
  recallStillDownloading();
  updateSW = registerSW({
    onNeedRefresh: setWorkerWaiting,
    // The new version took control: reload only the tab whose bar was tapped (never one in the background).
    onNeedReload: () => reloadOnce(),
    onRegisteredSW(_swUrl, r) {
      if (r) takeRegistration(r);
    }
  });
  if (typeof document === "undefined") return;
  // At open: the version file at once (the service worker's own check follows within the minute, once registered).
  check();
  // An iPhone home-screen app comes back without loading the page: both checks then, never while hidden.
  document.addEventListener("visibilitychange", check);
  // An app left open in view (a desktop tab, a phone on the counter): both checks every minute.
  setInterval(check, EVERY);
  // The signal came back: a check within seconds, so a release that went live while offline shows its bar.
  window.addEventListener("online", check);
}

/** Whether a new version is ready: a new worker installed and waiting, or the version file naming a newer release. */
export function newVersionReady(): boolean {
  return workerWaiting || newerRelease;
}

/** The tap is waiting for the new version to download: the bar's button says so. */
export function gettingNewVersionNow(): boolean {
  return gettingNewVersion;
}

/** This page opened after a reload that left the new version still downloading: the bar says why it is back. */
export function newVersionStillDownloading(): boolean {
  return stillDownloading;
}

export function onNewVersion(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/**
 * The person's tap: update to the new version and reload this page, once (a to d at the top). Nothing in IndexedDB is
 * touched.
 */
export async function reloadToNewVersion(): Promise<void> {
  if (tapped) return; // a second tap before the page reloads: the first one's listener and fallback are armed already
  tapped = true;
  tapLocation = window.location;
  const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  if (!sw) {
    reloadOnce(); // d. no service workers here: the network serves the new build
    return;
  }
  sw.addEventListener("controllerchange", () => reloadOnce(), { once: true });
  if (registration?.waiting) {
    await activateWaitingWorker();
    return;
  }
  if (workerWaiting || (!registration && !sw.controller)) {
    reloadOnce(); // b. another tab activated it; or d. nothing controls this page and no worker is known
    return;
  }
  fetchNewWorker();
}

/** a. The new version never took control (a worker that failed to activate): reload anyway, once. */
async function activateWaitingWorker(): Promise<void> {
  setTimeout(() => reloadOnce(), FALLBACK_WAITING);
  try {
    await updateSW?.(true);
  } catch {
    reloadOnce();
  }
}

/**
 * c. Only the version file knows: fetch the new worker; reload anyway if none arrives, waiting 10 s longer (30 s from the
 * tap at the latest) while one is still downloading. The new worker taking control reloads sooner ("controllerchange").
 * A reload under the old worker would bring the old build back from its precache.
 */
function fetchNewWorker(): void {
  adoptRegistration();
  gettingNewVersion = true;
  notify();
  setTimeout(() => {
    const r = registration;
    if (r?.installing) {
      setTimeout(() => reloadOnce(true), FALLBACK_INSTALLING); // still downloading at 30 s: the next page says so
    } else if (r?.waiting) {
      // Installed. Not yet told to take over (this page missed it): tell it now, and reload at 30 s if it never does.
      // Already told (it finished earlier and has not taken control yet): never a reload under the old worker while the
      // new one waits; the reload comes when it takes control, or at the last resort below.
      const untold = awaitingWorker;
      showIfWaiting();
      setTimeout(() => reloadOnce(), untold ? FALLBACK_INSTALLING : FALLBACK_TOLD);
    } else reloadOnce();
  }, FALLBACK_FETCHING);
  pursueNewWorker();
}
